// src/components/Auth/EmergencySignIn.jsx
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Login, PersonAdd, Movie, Home } from '@mui/icons-material';

/**
 * Emergency sign-in component for when users can't access the main auth page
 */
const EmergencySignIn = () => {
  const navigate = useNavigate();

  const handleNavigation = (path, fallbackPath = null) => {
    try {
      navigate(path);
    } catch (error) {
      console.error(`Navigation to ${path} failed:`, error);
      if (fallbackPath) {
        try {
          window.location.href = fallbackPath;
        } catch (fallbackError) {
          console.error(`Fallback navigation to ${fallbackPath} also failed:`, fallbackError);
          alert(`Unable to navigate to ${path}. Please refresh the page and try again.`);
        }
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center" color="error">
            🚨 Emergency Access
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            If you're having trouble accessing the sign-in page, use the buttons below to navigate directly.
          </Alert>

          <Typography variant="h6" gutterBottom>
            Authentication Options
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Login />}
              onClick={() => handleNavigation('/auth?mode=signin', '/auth')}
              fullWidth
            >
              Sign In (Direct Link)
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              startIcon={<PersonAdd />}
              onClick={() => handleNavigation('/auth?mode=signup', '/auth')}
              fullWidth
            >
              Sign Up (Direct Link)
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Alternative Navigation
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<Movie />}
              onClick={() => handleNavigation('/movies')}
              fullWidth
            >
              Browse Movies (No Sign-In Required)
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<Home />}
              onClick={() => handleNavigation('/')}
              fullWidth
            >
              Go to Home Page
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Troubleshooting
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => window.location.reload()}
              fullWidth
            >
              🔄 Reload Page
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = '/auth';
              }}
              fullWidth
            >
              🗑️ Clear Cache & Go to Sign-In
            </Button>
          </Box>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Still having issues?</strong><br />
              Try opening the app in an incognito/private browser window, or contact support if the problem persists.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmergencySignIn;